import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useMemo } from 'react';
import { ErrorCode, useDropzone, } from 'react-dropzone';
import { Box, Stack, Text, useFormControl, useMergeRefs, useMultiStyleConfig, } from '@chakra-ui/react';
import { dataAttr } from '@chakra-ui/utils';
import { omit } from 'lodash';
import { getErrorMessage } from './utils/getErrorMessage';
import { AttachmentStylesProvider } from './AttachmentContext';
import { AttachmentDropzone } from './AttachmentDropzone';
import { AttachmentError } from './AttachmentError';
import { AttachmentFileInfo } from './AttachmentFileInfo';
import { getReadableFileSize } from './utils';
export const Attachment = forwardRef(({ onChange, onError, maxSize, showFileSize, accept, value, name, colorScheme, imagePreview, onFileValidation, multiple, rejections, onRejection, ...props }, ref) => {
    // Merge given props with any form control props, if they exist.
    const inputProps = useFormControl(props);
    // id to set on the rendered max size FormHelperText component.
    const maxSizeTextId = useMemo(() => `${name}-max-size`, [name]);
    const readableMaxSize = useMemo(() => (maxSize ? getReadableFileSize(maxSize) : undefined), [maxSize]);
    const hasValue = useMemo(() => (multiple ? value.length > 0 : !!value), [multiple, value]);
    // Dropzone is displayed when (1) Multiple upload (2) Single upload but no file attached.
    const showDropzone = useMemo(() => multiple || !hasValue, [multiple, hasValue]);
    const filesArrayForRender = useMemo(() => {
        return multiple ? value : value ? [value] : [];
    }, [multiple, value]);
    const showMaxSize = useMemo(() => {
        return !hasValue && showFileSize && readableMaxSize;
    }, [hasValue, showFileSize, readableMaxSize]);
    const chooseMaxSizeText = useMemo(() => `${multiple ? 'You can upload multiple files at once.' : ''} Maximum file size: ${readableMaxSize}`, [multiple, readableMaxSize]);
    const ariaDescribedBy = useMemo(() => {
        const describedByIds = new Set();
        // Must be in this order so the screen reader reads out something coherent.
        // 1. Label text (if available)
        // 2. Initial describedby text (if available)
        // 3. Max size text (if prop is true)
        if (inputProps.id) {
            describedByIds.add(`${inputProps.id}-label`);
        }
        inputProps['aria-describedby']
            ?.split(' ')
            .map((id) => describedByIds.add(id));
        if (showMaxSize) {
            describedByIds.add(maxSizeTextId);
        }
        // Remove helptext, since label should already consist of the text
        describedByIds.delete(`${inputProps.id}-helptext`);
        return Array.from(describedByIds).filter(Boolean).join(' ').trim();
    }, [inputProps, maxSizeTextId, showMaxSize]);
    const fileValidator = useCallback((file) => {
        if (maxSize && file.size > maxSize) {
            return {
                code: ErrorCode.FileTooLarge,
                message: `Failed to upload. This file exceeds the size limit. Please upload a file that is under ${readableMaxSize}`,
            };
        }
        return null;
    }, [maxSize, readableMaxSize]);
    const handleFileDrop = useCallback(async (acceptedFiles, rejectedFiles) => {
        const validatedFiles = [];
        const rejects = [...rejectedFiles];
        await Promise.all(acceptedFiles.map(async (file) => {
            const fileValidationErrorMessage = await onFileValidation?.(file);
            if (!fileValidationErrorMessage) {
                validatedFiles.push(file);
            }
            else {
                rejects.push({
                    file,
                    errors: [
                        {
                            code: 'file-validation-error',
                            message: fileValidationErrorMessage,
                        },
                    ],
                });
            }
        }));
        if (rejects.length > 0) {
            onError?.(getErrorMessage(rejects[0]));
        }
        onRejection?.(rejects);
        if (multiple) {
            onChange(validatedFiles);
        }
        else {
            onChange(validatedFiles[0]);
        }
    }, [multiple, onFileValidation, onError, onRejection, onChange]);
    const { getRootProps, getInputProps, isDragActive, rootRef } = useDropzone({
        multiple,
        accept,
        disabled: inputProps.disabled,
        validator: fileValidator,
        noKeyboard: inputProps.readOnly || hasValue,
        noClick: inputProps.readOnly || hasValue,
        noDrag: inputProps.readOnly || hasValue,
        onDrop: handleFileDrop,
    });
    const mergedRefs = useMergeRefs(rootRef, ref);
    const styles = useMultiStyleConfig('Attachment', {
        isDragActive,
        colorScheme,
        imagePreview,
    });
    const handleRemoveFile = useCallback((target) => {
        if (multiple) {
            const attachedFiles = value.filter((file) => file !== target);
            onChange(attachedFiles);
            if (attachedFiles.length === 0) {
                rootRef.current?.focus();
            }
            return;
        }
        onChange(undefined);
        rootRef.current?.focus();
    }, [multiple, onChange, rootRef, value]);
    const handleDismissError = useCallback((target) => {
        if (!rejections)
            return;
        if (multiple) {
            const rejects = rejections.filter((reject) => reject !== target);
            if (rejects) {
                onRejection?.(rejects);
            }
            return;
        }
        onRejection?.([]);
    }, [multiple, onRejection, rejections]);
    // Bunch of memoization to avoid unnecessary re-renders.
    const processedRootProps = useMemo(() => {
        return getRootProps({
            // Root div does not need id prop, prevents duplicate ids.
            ...omit(inputProps, 'id'),
            // Bunch of extra work to prevent field from being used when in readOnly
            // state.
            onKeyDown: (e) => {
                if (inputProps.readOnly) {
                    e.stopPropagation();
                    return;
                }
            },
            'aria-describedby': ariaDescribedBy,
        });
    }, [ariaDescribedBy, getRootProps, inputProps]);
    const processedInputProps = useMemo(() => {
        return getInputProps({
            name,
            ...inputProps,
        });
    }, [getInputProps, inputProps, name]);
    return (_jsxs(AttachmentStylesProvider, { value: styles, children: [_jsxs(Stack, { gap: "1rem", children: [showDropzone ? (_jsx(Box, { ...processedRootProps, ref: mergedRefs, "data-active": dataAttr(isDragActive), __css: styles.dropzone, children: _jsx(AttachmentDropzone, { isDragActive: isDragActive, inputProps: processedInputProps }) })) : null, rejections && rejections.length > 0
                        ? rejections.map((fileRejection, index) => (_jsx(AttachmentError, { fileRejection: fileRejection, handleDismiss: handleDismissError }, `${fileRejection.file.name}${fileRejection.file.size}${index}`)))
                        : null, hasValue
                        ? filesArrayForRender.map((file, index) => (_jsx(AttachmentFileInfo, { file: file, imagePreview: imagePreview, isDisabled: inputProps.disabled, isReadOnly: inputProps.readOnly, handleRemoveFile: () => handleRemoveFile(file) }, `${file.name}${file.size}${index}`)))
                        : null] }), showMaxSize ? (_jsx(Text, { id: maxSizeTextId, color: "base.content.medium", mt: "0.5rem", textStyle: "body-2", children: chooseMaxSizeText })) : null] }));
});
Attachment.displayName = 'Attachment';
